import { useEffect, useState, type FormEvent } from 'react';
import type { AssignmentSummary } from '@ghalishor/contracts';
import { IdentityApiClient, identityErrorMessage } from './auth/api-client';
import { JalaliDateTimeField } from './jalali-datetime-field';
import { formatJalaliDateTime, jalaliPartsToIso, nowJalaliParts } from './jalali';

function statusLabel(status: string): string {
  switch (status) {
    case 'accepted':
      return 'پذیرفته شد';
    case 'contact_pending':
      return 'هماهنگی تماس';
    case 'price_pending':
      return 'منتظر تأیید قیمت';
    case 'schedule_pending':
      return 'منتظر تأیید زمان';
    case 'scheduled':
      return 'زمان‌بندی شد';
    case 'completed':
      return 'تکمیل شد';
    default:
      return status;
  }
}

function StatusMessage(props: { kind: 'info' | 'error'; children: string }) {
  return <div className={`status-message ${props.kind}`}>{props.children}</div>;
}

function defaultPickupIso(): string {
  const now = nowJalaliParts();
  return jalaliPartsToIso({ ...now, hour: 10, minute: 0 });
}

export function AssignmentPanel(props: {
  requestId: string;
  viewer: 'customer' | 'business';
  api: IdentityApiClient;
  busy: boolean;
  run: (fn: () => Promise<void>) => void;
  onInfo: (message: string) => void;
  onRevision: () => void;
  businessId?: string | null;
}) {
  const [assignment, setAssignment] = useState<AssignmentSummary | null>(null);
  const [pickupIso, setPickupIso] = useState(defaultPickupIso);
  const [returnIso, setReturnIso] = useState(() => {
    const now = nowJalaliParts();
    return jalaliPartsToIso({ ...now, hour: 16, minute: 0 });
  });
  const [notes, setNotes] = useState('');
  const [rating, setRating] = useState('5');
  const [comment, setComment] = useState('');
  const [priceInput, setPriceInput] = useState('');
  const [priceNote, setPriceNote] = useState('');
  const [localError, setLocalError] = useState<string | null>(null);

  useEffect(() => {
    let cancelled = false;
    void (async () => {
      try {
        const data =
          props.viewer === 'customer'
            ? await props.api.customerAssignment(props.requestId)
            : await props.api.businessAssignment(props.businessId!, props.requestId);
        if (!cancelled) {
          setAssignment(data);
          if (data.scheduledPickupAt) setPickupIso(data.scheduledPickupAt);
          if (data.scheduledReturnAt) setReturnIso(data.scheduledReturnAt);
          if (data.scheduleNotes) setNotes(data.scheduleNotes);
          const seed = data.confirmedPrice ?? data.estimatedPrice ?? null;
          if (seed !== null) setPriceInput(String(seed));
        }
      } catch (error) {
        if (!cancelled) setLocalError(identityErrorMessage(error));
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [props.requestId, props.viewer, props.businessId, props.api]);

  if (localError) return <StatusMessage kind="error">{localError}</StatusMessage>;
  if (!assignment) return <StatusMessage kind="info">در حال بارگذاری هماهنگی…</StatusMessage>;

  const onSchedule = (event: FormEvent) => {
    event.preventDefault();
    props.run(async () => {
      const body = {
        pickupAt: pickupIso,
        returnAt: returnIso,
        notes: notes.trim() || null,
      };
      if (props.viewer === 'customer') {
        await props.api.scheduleCustomer(props.requestId, body);
        props.onInfo('پیشنهاد جایگزین برای قالیشویی ارسال شد.');
      } else {
        await props.api.scheduleBusiness(props.businessId!, props.requestId, body);
        props.onInfo('زمان‌بندی برای تأیید مشتری پیشنهاد شد.');
      }
      props.onRevision();
    });
  };

  const onReview = (event: FormEvent) => {
    event.preventDefault();
    props.run(async () => {
      await props.api.submitReview(props.requestId, {
        rating: Number(rating),
        comment: comment.trim() || null,
      });
      props.onInfo('نظر شما ثبت شد.');
      props.onRevision();
    });
  };

  const onProposePrice = (event: FormEvent) => {
    event.preventDefault();
    props.run(async () => {
      await props.api.proposeConfirmedPrice(props.businessId!, props.requestId, {
        confirmedPrice: Number(priceInput),
        note: priceNote.trim() || null,
      });
      props.onInfo('قیمت نهایی برای تأیید مشتری ارسال شد.');
      props.onRevision();
    });
  };

  return (
    <div className="context-panel coord-panel">
      <strong>هماهنگی پس از پذیرش</strong>
      <span>وضعیت: {statusLabel(assignment.request.status)}</span>
      <span>طرف مقابل: {assignment.peer.displayName ?? '—'}</span>
      <span>شماره: {assignment.peer.mobileE164}</span>
      {assignment.peer.city ? <span>شهر: {assignment.peer.city}</span> : null}
      {assignment.peer.addressText ? <span>آدرس: {assignment.peer.addressText}</span> : null}
      {assignment.peer.latitude !== null && assignment.peer.longitude !== null ? (
        <a
          className="cta-link"
          href={`https://www.google.com/maps/dir/?api=1&destination=${encodeURIComponent(`${assignment.peer.latitude},${assignment.peer.longitude}`)}&travelmode=driving&dir_action=navigate`}
          target="_blank"
          rel="noreferrer"
        >
          {props.viewer === 'business' ? 'مسیریابی تا مشتری' : 'مشاهده موقعیت روی نقشه'}
        </a>
      ) : null}
      {assignment.distanceKm !== null ? (
        <span>فاصله تقریبی: {assignment.distanceKm.toLocaleString('fa-IR')} کیلومتر</span>
      ) : null}
      {assignment.estimatedPrice !== null ? (
        <span>برآورد قیمت: {assignment.estimatedPrice.toLocaleString('fa-IR')} ریال</span>
      ) : null}
      {assignment.confirmedPrice !== null ? (
        <span>
          قیمت نهایی {assignment.request.status === 'price_pending' ? 'پیشنهادی' : 'تأییدشده'}:{' '}
          {assignment.confirmedPrice.toLocaleString('fa-IR')} ریال
        </span>
      ) : null}
      {assignment.scheduledPickupAt ? (
        <span>زمان جمع‌آوری: {formatJalaliDateTime(assignment.scheduledPickupAt)}</span>
      ) : null}
      {assignment.scheduledReturnAt ? (
        <span>
          <strong>برگشت فرش:</strong> {formatJalaliDateTime(assignment.scheduledReturnAt)}
        </span>
      ) : null}
      {assignment.scheduleState === 'proposed' ? (
        <StatusMessage kind="info">پیشنهاد زمان ثبت شده؛ مهلت تأیید ۱ ساعت.</StatusMessage>
      ) : null}
      {assignment.scheduleState === 'counter_proposed' ? (
        <StatusMessage kind="info">پیشنهاد جایگزین مشتری در انتظار تأیید قالیشویی است.</StatusMessage>
      ) : null}
      {assignment.scheduleConfirmDeadlineAt ? (
        <span>مهلت: {formatJalaliDateTime(assignment.scheduleConfirmDeadlineAt)}</span>
      ) : null}
      <div className="account-actions">
        {assignment.canCall ? (
          <a className="cta-link" href={`tel:${assignment.peer.mobileE164}`}>
            تماس تلفنی
          </a>
        ) : null}
      </div>
      {assignment.canProposePrice && props.viewer === 'business' ? (
        <form className="stack-form" onSubmit={onProposePrice}>
          <label>
            قیمت نهایی (ریال)
            <input
              type="number"
              min={1}
              step={1}
              value={priceInput}
              onChange={(event) => setPriceInput(event.target.value)}
              required
            />
          </label>
          <label>
            یادداشت قیمت (اختیاری)
            <textarea value={priceNote} onChange={(event) => setPriceNote(event.target.value)} rows={2} />
          </label>
          <button type="submit" disabled={props.busy}>
            {props.busy
              ? 'در حال ارسال…'
              : assignment.request.status === 'price_pending'
                ? 'ویرایش و ارسال مجدد قیمت'
                : 'ارسال قیمت برای تأیید مشتری'}
          </button>
        </form>
      ) : null}
      {assignment.canConfirmPrice && props.viewer === 'customer' ? (
        <div className="price-confirm-card">
          <p>
            قالیشویی قیمت نهایی{' '}
            <strong>{(assignment.confirmedPrice ?? 0).toLocaleString('fa-IR')} ریال</strong> را پیشنهاد
            کرده است.
          </p>
          <div className="cta-row">
            <button
              type="button"
              disabled={props.busy}
              onClick={() => {
                props.run(async () => {
                  await props.api.confirmPrice(props.requestId);
                  props.onInfo('قیمت نهایی تأیید شد.');
                  props.onRevision();
                });
              }}
            >
              تأیید قیمت
            </button>
            <button
              type="button"
              className="secondary"
              disabled={props.busy}
              onClick={() => {
                props.run(async () => {
                  await props.api.rejectPrice(props.requestId);
                  props.onInfo('قیمت رد شد؛ منتظر پیشنهاد جدید باشید.');
                  props.onRevision();
                });
              }}
            >
              رد قیمت
            </button>
          </div>
        </div>
      ) : null}
      {assignment.canConfirmSchedule ? (
        <button
          type="button"
          disabled={props.busy}
          onClick={() => {
            props.run(async () => {
              if (props.viewer === 'customer') {
                await props.api.confirmScheduleCustomer(props.requestId);
              } else {
                await props.api.confirmScheduleBusiness(props.businessId!, props.requestId);
              }
              props.onInfo('زمان‌بندی تأیید شد.');
              props.onRevision();
            });
          }}
        >
          تأیید زمان‌بندی
        </button>
      ) : null}
      {assignment.canProposeSchedule || assignment.canCounterSchedule ? (
        <form className="stack-form" onSubmit={onSchedule}>
          <JalaliDateTimeField
            label="زمان جمع‌آوری (هجری شمسی)"
            valueIso={pickupIso}
            onChangeIso={setPickupIso}
            required
          />
          <JalaliDateTimeField
            label="زمان برگشت فرش (هجری شمسی)"
            valueIso={returnIso}
            onChangeIso={setReturnIso}
            required
          />
          <label>
            یادداشت زمان‌بندی
            <textarea value={notes} onChange={(event) => setNotes(event.target.value)} rows={2} />
          </label>
          <button type="submit" disabled={props.busy}>
            {props.busy
              ? 'در حال ثبت…'
              : props.viewer === 'business'
                ? 'پیشنهاد زمان‌بندی به مشتری'
                : 'ارسال پیشنهاد جایگزین'}
          </button>
        </form>
      ) : null}
      {assignment.canReview && props.viewer === 'customer' ? (
        <form className="stack-form" onSubmit={onReview}>
          <label>
            امتیاز
            <select value={rating} onChange={(event) => setRating(event.target.value)}>
              {['5', '4', '3', '2', '1'].map((value) => (
                <option key={value} value={value}>
                  {value}
                </option>
              ))}
            </select>
          </label>
          <label>
            نظر
            <textarea value={comment} onChange={(event) => setComment(event.target.value)} rows={2} />
          </label>
          <button type="submit" disabled={props.busy}>
            ثبت نظر و تکمیل
          </button>
        </form>
      ) : null}
    </div>
  );
}

export function formatOfferBadge(badge: string): string {
  switch (badge) {
    case 'nearby':
      return 'نزدیک شما';
    case 'top_rated':
      return 'امتیاز بالا';
    case 'fast_response':
      return 'پاسخ‌گو';
    case 'fast_turnaround':
      return 'برگشت سریع';
    case 'high_guarantee':
      return 'ضمانت بیشتر';
    case 'economical':
      return 'اقتصادی';
    default:
      return badge;
  }
}
